// src/views/bots/flow/nodes/QuestionNode.tsx
// Generic question node for all question types with TRUE inline editing

import React, { memo, useState, useRef, useEffect } from 'react';
import { Position, useReactFlow } from '@xyflow/react';
import * as ReactFlowComponents from '@xyflow/react';
import styles from './nodes.module.css';
import { NODE_TYPES } from '../palette/NodePalette';

// Workaround for @xyflow/react v12 type collision (Handle exported as both component and type)
const Handle = ReactFlowComponents.Handle as React.ComponentType<{
  type: 'source' | 'target';
  position: typeof Position[keyof typeof Position];
  id: string;
  className?: string;
  style?: React.CSSProperties;
}>;

interface QuestionNodeProps {
  id: string;
  data: {
    questionType: string;
    question?: string;
    required?: boolean;
    interactive?: {
      type: 'button' | 'list';
      header?: {
        type: 'text';
        text: string;
      };
      body?: {
        text: string;
      };
      footer?: {
        text: string;
      };
      action?: {
        button?: string; // List menu trigger text (e.g. "Main Menu")
        buttons?: Array<{
          type: string;
          reply: {
            id: string;
            title: string;
          };
        }>;
        sections?: Array<{
          title: string;
          rows: Array<{
            id: string;
            title: string;
            description?: string;
          }>;
        }>;
      };
    };
    groupId?: string;
  };
  selected?: boolean;
}

const QuestionNode: React.FC<QuestionNodeProps> = ({ id, data, selected = false }) => {
  const [isEditingMessage, setIsEditingMessage] = useState(false);
  const [isEditingHeader, setIsEditingHeader] = useState(false);
  const [isEditingMenuButton, setIsEditingMenuButton] = useState(false);
  // For interactive types, body text lives in interactive.body.text, not data.question
  const [localQuestion, setLocalQuestion] = useState(
    data.question || data.interactive?.body?.text || ''
  );
  const [localHeader, setLocalHeader] = useState(data.interactive?.header?.text || '');
  const [localMenuButton, setLocalMenuButton] = useState(data.interactive?.action?.button || 'View Options');
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const headerInputRef = useRef<HTMLInputElement>(null);
  const menuButtonInputRef = useRef<HTMLInputElement>(null);
  const { setNodes, setEdges } = useReactFlow();

  // Get node type definition from palette
  const nodeTypeDef = NODE_TYPES.find(n => n.type === data.questionType);
  const icon = nodeTypeDef?.icon || '❓';
  const label = nodeTypeDef?.label || data.questionType;
  const color = nodeTypeDef?.color || '#3b82f6';

  // Sync localQuestion when data changes externally (e.g. on load from backend)
  useEffect(() => {
    const newText = data.question || data.interactive?.body?.text || '';
    if (!isEditingMessage && newText !== localQuestion) {
      setLocalQuestion(newText);
    }
  }, [data.question, data.interactive?.body?.text]);

  // Sync localHeader when data changes externally
  useEffect(() => {
    const newHeader = data.interactive?.header?.text || '';
    if (!isEditingHeader && newHeader !== localHeader) {
      setLocalHeader(newHeader);
    }
  }, [data.interactive?.header?.text]);

  // Auto-focus textarea when editing
  useEffect(() => {
    if (isEditingMessage && textareaRef.current) {
      textareaRef.current.focus();
      textareaRef.current.select();
    }
  }, [isEditingMessage]);

  // Auto-focus header input when editing
  useEffect(() => {
    if (isEditingHeader && headerInputRef.current) {
      headerInputRef.current.focus();
      headerInputRef.current.select();
    }
  }, [isEditingHeader]);

  // Sync localMenuButton when data changes externally
  useEffect(() => {
    const newBtn = data.interactive?.action?.button || 'View Options';
    if (!isEditingMenuButton && newBtn !== localMenuButton) {
      setLocalMenuButton(newBtn);
    }
  }, [data.interactive?.action?.button]);

  // Auto-focus menu button input when editing
  useEffect(() => {
    if (isEditingMenuButton && menuButtonInputRef.current) {
      menuButtonInputRef.current.focus();
      menuButtonInputRef.current.select();
    }
  }, [isEditingMenuButton]);

  // Handle node update
  const updateNodeData = (updates: any) => {
    setNodes((nodes) =>
      nodes.map((node) => {
        if (node.id === id) {
          return {
            ...node,
            data: {
              ...node.data,
              ...updates,
            },
          };
        }
        return node;
      })
    );
  };

  // Save message
  const saveMessage = () => {
    const updates: any = { question: localQuestion, required: true }; // Always required

    // If interactive button/list, also update the body.text to match the message
    if ((data.questionType === 'interactive-button' || data.questionType === 'interactive-list') && data.interactive) {
      updates.interactive = {
        ...data.interactive,
        body: { ...data.interactive.body, text: localQuestion },
      };
    }

    updateNodeData(updates);
    setIsEditingMessage(false);
  };

  // Save header (for interactive types)
  const saveHeader = () => {
    if ((data.questionType === 'interactive-button' || data.questionType === 'interactive-list') && data.interactive) {
      updateNodeData({
        interactive: {
          ...data.interactive,
          header: { type: 'text', text: localHeader || 'Choose an option' },
        },
      });
    }
    setIsEditingHeader(false);
  };

  // Save menu button text (for interactive-list only)
  const saveMenuButton = () => {
    if (data.questionType === 'interactive-list' && data.interactive) {
      updateNodeData({
        interactive: {
          ...data.interactive,
          action: {
            ...data.interactive.action,
            button: localMenuButton || 'View Options',
          },
        },
      });
    }
    setIsEditingMenuButton(false);
  };

  // Handle copy node
  const handleCopy = (e: React.MouseEvent) => {
    e.stopPropagation();

    const currentNode = { id, data };

    // Generate new unique ID
    const newId = `node-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;

    // Create copied node with offset position
    setNodes((nodes) => {
      const originalNode = nodes.find(n => n.id === id);
      if (!originalNode) return nodes;

      return [
        ...nodes,
        {
          ...originalNode,
          id: newId,
          position: {
            x: originalNode.position.x + 50,
            y: originalNode.position.y + 50,
          },
          selected: false,
          data: {
            ...originalNode.data,
            stepId: newId, // Update stepId
          },
        },
      ];
    });
  };

  // Handle delete node - removes node and all connected edges
  const handleDelete = (e: React.MouseEvent) => {
    e.stopPropagation();

    // Remove all edges connected to this node
    setEdges((edges) => edges.filter((edge) => edge.source !== id && edge.target !== id));

    // Remove the node itself
    setNodes((nodes) => nodes.filter((node) => node.id !== id));
  };

  // Get options from interactive data
  const getOptionsAndSections = () => {
    if (!data.interactive) return { options: [], sections: [] };

    if (data.questionType === 'interactive-button') {
      const buttons = data.interactive.action?.buttons?.map((btn: any) => ({
        id: btn.reply.id,
        title: btn.reply.title,
      })) || [];
      return { options: buttons, sections: [] };
    } else if (data.questionType === 'interactive-list') {
      const sections = data.interactive.action?.sections || [];
      let allOptions: any[] = [];
      sections.forEach((section: any) => {
        section.rows?.forEach((row: any) => {
          allOptions.push({ id: row.id, title: row.title });
        });
      });
      return { options: allOptions, sections };
    }
    return { options: [], sections: [] };
  };

  const { options, sections } = getOptionsAndSections();
  const hasBranching = options.length > 0;
  const branchCount = options.length;

  // Add button
  const addButton = () => {
    const newButton = {
      type: 'reply',
      reply: {
        id: `btn_${Date.now()}_${options.length + 1}`,
        title: `Button ${options.length + 1}`,
      },
    };

    const updatedButtons = [
      ...(data.interactive?.action?.buttons || []),
      newButton,
    ];

    updateNodeData({
      question: localQuestion, // Sync message with body.text
      interactive: {
        ...data.interactive,
        type: 'button',
        header: data.interactive?.header || { type: 'text', text: 'Choose an option' },
        body: { text: localQuestion || 'Please select an option' },
        action: { buttons: updatedButtons },
      },
    });
  };

  // Remove button
  const removeButton = (buttonId: string) => {
    const updatedButtons = data.interactive?.action?.buttons?.filter(
      (btn: any) => btn.reply.id !== buttonId
    ) || [];

    updateNodeData({
      interactive: {
        ...data.interactive,
        type: 'button',
        header: data.interactive?.header || { type: 'text', text: 'Choose an option' },
        body: { text: localQuestion || data.interactive?.body?.text || 'Please select an option' },
        action: { buttons: updatedButtons },
      },
    });
  };

  // Update button title
  const updateButtonTitle = (buttonId: string, newTitle: string) => {
    const updatedButtons = data.interactive?.action?.buttons?.map((btn: any) =>
      btn.reply.id === buttonId
        ? { ...btn, reply: { ...btn.reply, title: newTitle } }
        : btn
    ) || [];

    updateNodeData({
      interactive: {
        ...data.interactive,
        type: 'button',
        header: data.interactive?.header || { type: 'text', text: 'Choose an option' },
        body: { text: localQuestion || data.interactive?.body?.text || 'Please select an option' },
        action: { buttons: updatedButtons },
      },
    });
  };

  // 🔥 LIST MANAGEMENT FUNCTIONS
  const addListSection = () => {
    const currentSections = data.interactive?.action?.sections || [];
    const newSection = {
      title: `Section ${currentSections.length + 1}`,
      rows: [
        { id: `row-${Date.now()}-1`, title: 'New Option', description: '' },
      ],
    };
    updateNodeData({
      interactive: {
        ...data.interactive,
        action: {
          ...data.interactive?.action,
          sections: [...currentSections, newSection],
        },
      },
    });
  };

  const removeListSection = (sectionIndex: number) => {
    const currentSections = data.interactive?.action?.sections || [];
    const updatedSections = currentSections.filter((_, idx) => idx !== sectionIndex);
    updateNodeData({
      interactive: {
        ...data.interactive,
        action: {
          ...data.interactive?.action,
          sections: updatedSections,
        },
      },
    });
  };

  const updateListSectionTitle = (sectionIndex: number, newTitle: string) => {
    const currentSections = [...(data.interactive?.action?.sections || [])];
    if (!currentSections[sectionIndex]) return;
    currentSections[sectionIndex] = {
      ...currentSections[sectionIndex],
      title: newTitle,
    };
    updateNodeData({
      interactive: {
        ...data.interactive,
        action: {
          ...data.interactive?.action,
          sections: currentSections,
        },
      },
    });
  };

  const addListRow = (sectionIndex: number) => {
    const currentSections = [...(data.interactive?.action?.sections || [])];
    if (!currentSections[sectionIndex]) return;
    const newRow = {
      id: `row-${Date.now()}`,
      title: 'New Option',
      description: '',
    };
    currentSections[sectionIndex] = {
      ...currentSections[sectionIndex],
      rows: [...(currentSections[sectionIndex].rows || []), newRow],
    };
    updateNodeData({
      interactive: {
        ...data.interactive,
        action: {
          ...data.interactive?.action,
          sections: currentSections,
        },
      },
    });
  };

  const removeListRow = (sectionIndex: number, rowId: string) => {
    const currentSections = [...(data.interactive?.action?.sections || [])];
    if (!currentSections[sectionIndex]) return;
    currentSections[sectionIndex] = {
      ...currentSections[sectionIndex],
      rows: (currentSections[sectionIndex].rows || []).filter((row: any) => row.id !== rowId),
    };
    updateNodeData({
      interactive: {
        ...data.interactive,
        action: {
          ...data.interactive?.action,
          sections: currentSections,
        },
      },
    });
  };

  const updateListRow = (sectionIndex: number, rowId: string, field: 'title' | 'description', value: string) => {
    const currentSections = [...(data.interactive?.action?.sections || [])];
    if (!currentSections[sectionIndex]) return;
    currentSections[sectionIndex] = {
      ...currentSections[sectionIndex],
      rows: (currentSections[sectionIndex].rows || []).map((row: any) =>
        row.id === rowId ? { ...row, [field]: value } : row
      ),
    };
    updateNodeData({
      interactive: {
        ...data.interactive,
        action: {
          ...data.interactive?.action,
          sections: currentSections,
        },
      },
    });
  };

  return (
    <div
      className={`${styles.whatsappMessage} ${selected ? styles.selected : ''} ${selected ? styles.expanded : ''}`}
      onClick={(e) => e.stopPropagation()}
    >
      {/* Input Handle - receives connection from previous node */}
      <Handle
        type="target"
        position={Position.Top}
        id="question-input"
        className={styles.handle}
      />

      {/* Node Action Buttons */}
      <div className={styles.nodeActions}>
        <button
          className={styles.copyButton}
          onClick={handleCopy}
          title="Copy this node"
        >
          📋
        </button>
        <button
          className={styles.deleteButton}
          onClick={handleDelete}
          title="Delete this node"
        >
          🗑️
        </button>
      </div>

      {/* WhatsApp Message Bubble */}
      <div className={styles.messageBubble}>
        {/* Small Type Badge */}
        <div className={styles.messageTypeBadge} style={{ background: color }}>
          <span className={styles.messageTypeIcon}>{icon}</span>
          <span className={styles.messageTypeLabel}>{label}</span>
        </div>

        {/* Header Editor for Interactive Types - ABOVE body text */}
        {(data.questionType === 'interactive-button' || data.questionType === 'interactive-list') && selected && (
          <div className={styles.headerEditor}>
            <label className={styles.headerLabel}>Header:</label>
            {isEditingHeader ? (
              <input
                ref={headerInputRef}
                type="text"
                className={styles.headerInput}
                value={localHeader}
                onChange={(e) => setLocalHeader(e.target.value)}
                onBlur={saveHeader}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    saveHeader();
                  }
                  if (e.key === 'Escape') {
                    setLocalHeader(data.interactive?.header?.text || '');
                    setIsEditingHeader(false);
                  }
                }}
                onClick={(e) => e.stopPropagation()}
                placeholder="Enter header text..."
                maxLength={60}
              />
            ) : (
              <div
                className={styles.headerDisplay}
                onClick={(e) => {
                  e.stopPropagation();
                  setIsEditingHeader(true);
                }}
              >
                {data.interactive?.header?.text || 'Choose an option'}
                <span className={styles.editHint}>✏️</span>
              </div>
            )}
          </div>
        )}

        {/* Body Text - Editable when selected */}
        {selected && isEditingMessage ? (
          <div className={styles.inlineEditor}>
            <textarea
              ref={textareaRef}
              className={styles.inlineTextarea}
              value={localQuestion}
              onChange={(e) => setLocalQuestion(e.target.value)}
              placeholder="Enter body text..."
              rows={2}
              onBlur={saveMessage}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && !e.shiftKey) {
                  e.preventDefault();
                  saveMessage();
                }
                if (e.key === 'Escape') {
                  setLocalQuestion(data.question || data.interactive?.body?.text || '');
                  setIsEditingMessage(false);
                }
              }}
              onClick={(e) => e.stopPropagation()}
            />
          </div>
        ) : (
          <div
            className={styles.messageText}
            onClick={(e) => {
              e.stopPropagation();
              if (selected) setIsEditingMessage(true);
            }}
            style={{ cursor: selected ? 'text' : 'default' }}
          >
            {data.question || data.interactive?.body?.text || (
              <span className={styles.placeholderText}>
                {selected ? 'Click to add body text' : 'Click node to edit'}
              </span>
            )}
            {selected && !isEditingMessage && (
              <span className={styles.editHint} title="Click to edit body text">✏️</span>
            )}
          </div>
        )}

        {/* Interactive Buttons - Editable when selected */}
        {data.questionType === 'interactive-button' && (
          <div className={styles.whatsappButtons}>
            {options.map((option: any, index: number) => (
              <div
                key={option.id}
                className={styles.whatsappButton}
              >
                {selected ? (
                  <input
                    type="text"
                    className={styles.inlineButtonInput}
                    value={option.title}
                    onChange={(e) => updateButtonTitle(option.id, e.target.value)}
                    onClick={(e) => e.stopPropagation()}
                    maxLength={20}
                    placeholder="Button text"
                  />
                ) : (
                  <span className={styles.buttonText}>{option.title}</span>
                )}
                {selected && (
                  <button
                    className={styles.inlineRemoveBtn}
                    onClick={(e) => {
                      e.stopPropagation();
                      removeButton(option.id);
                    }}
                  >
                    ×
                  </button>
                )}
                <Handle
                  type="source"
                  position={Position.Right}
                  id={`branch-${option.id}`}
                  className={styles.buttonHandle}
                />
              </div>
            ))}
            {selected && options.length < 3 && (
              <button
                className={styles.inlineAddBtn}
                onClick={(e) => {
                  e.stopPropagation();
                  addButton();
                }}
              >
                + Add Button
              </button>
            )}
          </div>
        )}

        {/* Interactive List - INLINE EDITING when selected */}
        {data.questionType === 'interactive-list' && (
          <div className={styles.whatsappList}>
            {/* Menu Button Editor - the "tap to open" button text */}
            {selected && (
              <div className={styles.menuButtonEditor}>
                <label className={styles.headerLabel}>Menu Button:</label>
                {isEditingMenuButton ? (
                  <input
                    ref={menuButtonInputRef}
                    type="text"
                    className={styles.headerInput}
                    value={localMenuButton}
                    onChange={(e) => setLocalMenuButton(e.target.value)}
                    onBlur={saveMenuButton}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') {
                        e.preventDefault();
                        saveMenuButton();
                      }
                      if (e.key === 'Escape') {
                        setLocalMenuButton(data.interactive?.action?.button || 'View Options');
                        setIsEditingMenuButton(false);
                      }
                    }}
                    onClick={(e) => e.stopPropagation()}
                    placeholder="Menu button text..."
                    maxLength={20}
                  />
                ) : (
                  <div
                    className={styles.headerDisplay}
                    onClick={(e) => {
                      e.stopPropagation();
                      setIsEditingMenuButton(true);
                    }}
                  >
                    {data.interactive?.action?.button || 'View Options'}
                    <span className={styles.editHint}>✏️</span>
                  </div>
                )}
              </div>
            )}

            {/* Non-selected: show menu button like WhatsApp */}
            {!selected && (
              <div className={styles.listMenuButton}>
                ☰ {data.interactive?.action?.button || 'View Options'}
              </div>
            )}

            {sections.length === 0 && selected ? (
              <button
                className={styles.inlineAddBtn}
                onClick={(e) => {
                  e.stopPropagation();
                  addListSection();
                }}
              >
                + Add First Section
              </button>
            ) : (
              <>
                {sections.map((section: any, sectionIdx: number) => (
                  <div key={sectionIdx} className={styles.listSectionContainer}>
                    {/* Section Header - Editable when selected */}
                    {selected ? (
                      <div className={styles.listSectionHeader}>
                        <input
                          type="text"
                          value={section.title}
                          onChange={(e) => updateListSectionTitle(sectionIdx, e.target.value)}
                          className={styles.inlineButtonInput}
                          placeholder="Section title"
                          maxLength={24}
                          onClick={(e) => e.stopPropagation()}
                        />
                        <button
                          className={styles.inlineRemoveBtn}
                          onClick={(e) => {
                            e.stopPropagation();
                            removeListSection(sectionIdx);
                          }}
                        >
                          ×
                        </button>
                      </div>
                    ) : (
                      <div className={styles.listSectionTitle}>
                        {section.title}
                      </div>
                    )}

                    {/* Section Rows */}
                    {section.rows?.map((row: any) => (
                      <div key={row.id} className={styles.listItem}>
                        {selected ? (
                          <div style={{ display: 'flex', flexDirection: 'column', gap: '4px', flex: 1 }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                              <input
                                type="text"
                                value={row.title}
                                onChange={(e) => updateListRow(sectionIdx, row.id, 'title', e.target.value)}
                                className={styles.inlineButtonInput}
                                placeholder="Option title"
                                maxLength={24}
                                onClick={(e) => e.stopPropagation()}
                                style={{ flex: 1 }}
                              />
                              <button
                                className={styles.inlineRemoveBtn}
                                onClick={(e) => {
                                  e.stopPropagation();
                                  removeListRow(sectionIdx, row.id);
                                }}
                              >
                                ×
                              </button>
                            </div>
                            <input
                              type="text"
                              value={row.description || ''}
                              onChange={(e) => updateListRow(sectionIdx, row.id, 'description', e.target.value)}
                              className={styles.listRowDescInput}
                              placeholder="Description (optional)"
                              maxLength={72}
                              onClick={(e) => e.stopPropagation()}
                            />
                          </div>
                        ) : (
                          <div>
                            <div className={styles.listItemTitle}>{row.title}</div>
                            {row.description && (
                              <div className={styles.listItemDesc}>{row.description}</div>
                            )}
                          </div>
                        )}
                        <Handle
                          type="source"
                          position={Position.Right}
                          id={`branch-${row.id}`}
                          className={styles.buttonHandle}
                        />
                      </div>
                    ))}

                    {/* Add Row button - only when selected */}
                    {selected && (section.rows?.length || 0) < 10 && (
                      <button
                        className={styles.inlineAddBtn}
                        onClick={(e) => {
                          e.stopPropagation();
                          addListRow(sectionIdx);
                        }}
                      >
                        + Add Row ({section.rows?.length || 0}/10)
                      </button>
                    )}
                  </div>
                ))}

                {/* Add Section button - only when selected */}
                {selected && sections.length < 10 && (
                  <button
                    className={styles.inlineAddBtn}
                    onClick={(e) => {
                      e.stopPropagation();
                      addListSection();
                    }}
                    style={{ marginTop: '8px' }}
                  >
                    + Add Section ({sections.length}/10)
                  </button>
                )}
              </>
            )}
          </div>
        )}

        {/* Message Footer */}
        <div className={styles.messageFooter}>
          <span className={styles.messageTime}>Bot</span>
        </div>
      </div>

      {/* Output Handle - bottom for sequential flow */}
      <Handle
        type="source"
        position={Position.Bottom}
        id="question-output"
        className={styles.handle}
        style={{
          background: hasBranching ? '#6366f1' : '#00a884',
        }}
      />
    </div>
  );
};

export default memo(QuestionNode);
